import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { deleteFile, fileUrl, getFileList, uploadFile } from '../../../api/files';
import { errorMessage } from '../../../api/errors';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useAdminData } from '../../../hooks/useAdminData';
import { logout } from '../../../store/admin.slice';
import { logout as logoutRequest } from '../../../api/auth';
import { ADMIN_STORAGE_KEY } from '../../../api/client';

export function FilesSection() {
    const admin = useAdminData();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [files, setFiles] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);

    const [fileName, setFileName] = useState('');
    const [fileBlob, setFileBlob] = useState<string | undefined>();
    const [uploading, setUploading] = useState(false);
    /** File in attesa di conferma di eliminazione (null = nessun dialog aperto). */
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        dispatch(logout());
        // M4: azzera anche il cookie di autenticazione lato server.
        void logoutRequest().catch(() => undefined);
        navigate('/admin/login');
    }, [dispatch, navigate]);

    useEffect(() => {
        if (!admin.logged) return;
        setLoading(true);
        getFileList()
            .then((list) => setFiles(list))
            .catch((err: unknown) => {
                const status = (err as { response?: { status?: number } }).response?.status;
                if (status === 401) handleUnauthorized();
                else setError(errorMessage(err, 'Impossibile caricare la lista dei file.'));
            })
            .finally(() => setLoading(false));
    }, [admin.logged, reload, handleUnauthorized]);

    const convertBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(String(reader.result).split(',')[1]);
            reader.onerror = reject;
        });

    const handleFileChange = async (file: File | undefined) => {
        if (!file) return;
        setFileName(file.name);
        setFileBlob(await convertBase64(file));
    };

    const handleUpload = async () => {
        if (!admin.logged || !fileName || !fileBlob) return;
        setUploading(true);
        setError(null);
        setSuccess(null);
        try {
            await uploadFile(fileName, fileBlob);
            setSuccess(`"${fileName}" caricato.`);
            setFileName('');
            setFileBlob(undefined);
            setReload((r) => !r);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) handleUnauthorized();
            // Un errore di upload (file troppo grande, formato non ammesso, ...) deve
            // essere visibile: prima veniva ignorato e il submit sembrava riuscito.
            else setError(errorMessage(err, 'Caricamento non riuscito. Controlla il file e riprova.'));
        } finally {
            setUploading(false);
        }
    };

    // L'eliminazione chiede conferma: il click sull'icona apre il dialog, la DELETE
    // parte solo dopo il "Elimina".
    const confirmDelete = async () => {
        if (!admin.logged || !pendingDelete) return;
        const name = pendingDelete;
        setError(null);
        setSuccess(null);
        try {
            await deleteFile(name);
            setSuccess(`"${name}" eliminato.`);
            setReload((r) => !r);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) handleUnauthorized();
            else setError(errorMessage(err, 'Eliminazione non riuscita. Riprova.'));
        } finally {
            setPendingDelete(null);
        }
    };

    const canUpload = fileName.length > 0 && fileBlob !== undefined && !uploading;

    return (
        <div className="flex flex-col gap-6">
            <Paper elevation={0} className="border border-white/10 bg-black/40 p-4">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Gestione file
                </Typography>
                <Typography variant="body2" color="text.secondary" className="mb-4">
                    File caricati
                </Typography>

                {loading ? (
                    <LinearProgress />
                ) : files.length === 0 ? (
                    <EmptyState message="Nessun file caricato." />
                ) : (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {files.map((name) => (
                            <div
                                key={name}
                                className="relative rounded-2xl bg-white/5 p-3 transition hover:bg-white/10"
                            >
                                <a
                                    href={fileUrl(name)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex flex-col items-center gap-2"
                                >
                                    <img src={fileUrl(name)} alt={name} className="h-24 w-24 rounded-lg object-cover" />
                                    <Typography variant="caption" noWrap className="max-w-full">
                                        {name}
                                    </Typography>
                                </a>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setPendingDelete(name)}
                                    title="Elimina file"
                                    aria-label={`Elimina ${name}`}
                                    className="absolute right-1 top-1"
                                >
                                    <Close sx={{ fontSize: 20 }} />
                                </IconButton>
                            </div>
                        ))}
                    </div>
                )}
            </Paper>

            <Paper elevation={0} className="border border-white/10 bg-black/40 p-4">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Nuovo file
                </Typography>
                {error && (
                    <Alert severity="error" className="mt-3" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" className="mt-3" onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}
                <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end">
                    <Button variant="outlined" component="label" className="shrink-0">
                        Scegli immagine
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => handleFileChange(e.target.files?.[0])}
                        />
                    </Button>
                    <TextField
                        label="Nome file"
                        fullWidth
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                    />
                    <Button variant="contained" disabled={!canUpload} onClick={handleUpload}>
                        Invia
                    </Button>
                </div>
            </Paper>

            <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
                <DialogTitle>Eliminare il file?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingDelete ? `"${pendingDelete}" verrà eliminato definitivamente.` : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)}>Annulla</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>
                        Elimina
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
